/* eslint-disable  */
import { Alert, Form, FormInstance, Button, Input } from 'antd';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent } from '@/store/account/selector';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import './ChangeTestingNumberForm.scss';
import { forwardRef, useState, useEffect } from 'react';
import PhoneNumberInput from '@/components/Inputs/PhoneNumberInput';
import { handlePrefixPlusOnPhone } from '@/utils/helper';
import { AlertCircle, CheckCircle, LockIcon } from 'lucide-react';
import { sendOTP, verifyOTP } from '@/api/otp';

type ChangeTestingNumberFormProps = {
	onSubmit: (values: { testing_number: string }) => void;
	onVerificationChange?: (verified: boolean) => void;
};

export const ChangeTestingNumberForm = forwardRef<FormInstance, ChangeTestingNumberFormProps>(
	({ onSubmit, onVerificationChange }, ref) => {
		const [form] = Form.useForm<{
			testing_number: string;
		}>();

		const [isTouched, setIsTouched] = useState<boolean>(form.isFieldsTouched());
		const [otpSent, setOtpSent] = useState(false);
		const [otp, setOtp] = useState('');
		const [verifying, setVerifying] = useState(false);
		const [sending, setSending] = useState(false);
		const [otpError, setOtpError] = useState('');
		const [sendError, setSendError] = useState('');
		const [verified, setVerified] = useState(false);

		const selectedAgent = useAppSelector(getSelectedAgent);

		useEffect(() => {
			onVerificationChange?.(verified);
		}, [verified, onVerificationChange]);

		useEffect(() => {
			const phoneNumber = form.getFieldValue('testing_number');
			if (phoneNumber) {
				setOtpSent(false);
				setOtp('');
				setSendError('');
				setOtpError('');
				setVerified(false);
			}
		}, [form.getFieldValue('testing_number')]);

		const handleFinish = (values: { testing_number: string }) => {
			if (!verified) {
				return;
			}
			onSubmit({ testing_number: handlePrefixPlusOnPhone(values.testing_number) });
		};

		const handleSendOTP = async () => {
			const phoneNumber = handlePrefixPlusOnPhone(form.getFieldValue('testing_number'));
			if (!phoneNumber) {
				return;
			}

			try {
				setSending(true);
				setSendError('');
				setOtpError('');
				const response = await sendOTP(phoneNumber);

				if (response.success) {
					setOtpSent(true);
				} else {
					throw new Error(response.message || 'Failed to send verification code');
				}
			} catch (err: any) {
				setSendError(err.message || 'Failed to send code. Please try again later');
			} finally {
				setSending(false);
			}
		};

		const handleVerifyOTP = async () => {
			if (!otp || otp.length < 4) {
				setOtpError('Please enter a valid verification code');
				return;
			}

			const phoneNumber = handlePrefixPlusOnPhone(form.getFieldValue('testing_number'));
			if (!phoneNumber) {
				return;
			}

			try {
				setVerifying(true);
				setOtpError('');
				const response = await verifyOTP(phoneNumber, otp);

				if (response.success) {
					setVerified(true);
				} else {
					throw new Error(response.message || 'Invalid verification code');
				}
			} catch (err: any) {
				setOtpError(err.message || 'Verification failed. Please try again');
			} finally {
				setVerifying(false);
			}
		};

		const SaveButton = () => {
			return <span>You have some unsaved changes </span>;
		};

		const baseClass = 'change-testing-number-form';
		return (
			<div className={`${baseClass}`}>
				<p className="text-sm mt-1 mb-2">
					<span>This will allow you to test call your receptionist from a different number.</span>
					<br />
					<span>
						This is useful if you don't have access to{' '}
						<strong>{selectedAgent?.testing_number}</strong> to test your receptionist
					</span>
					<br />
					<span>
						Once you start your free trial, you can reserve a stand alone number and call your
						receptionist from any number.
					</span>
				</p>
				<Form
					form={form}
					layout="vertical"
					name="change-testing-number-form"
					className={baseClass}
					onFinish={handleFinish}
					ref={ref}
				>
					<Form.Item
						label={
							<LabelWithDescription
								label="Testing Number"
								description="The phone number you want to use for testing"
								required={true}
							/>
						}
						className={`${baseClass}__input-item !mb-0`}
					>
						<PhoneNumberInput name="testing_number" />
						{sendError && (
							<div className="flex items-center gap-1 mt-2 text-red-500 text-sm">
								<AlertCircle className="h-4 w-4" />
								<span>{sendError}</span>
							</div>
						)}
					</Form.Item>

					{form.getFieldValue('testing_number') && !verified && (
						<div>
							{!otpSent ? (
								<Button
									type="primary"
									onClick={handleSendOTP}
									loading={sending}
									className="h-12 bg-[#6366F1] hover:bg-[#5558E1] border-0"
								>
									Send Verification Code
								</Button>
							) : (
								<div className="space-y-3">
									<div>
										<Input
											placeholder="Enter verification code"
											value={otp}
											onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
											className="h-12"
											maxLength={6}
											status={otpError ? 'error' : undefined}
											prefix={<LockIcon className="h-5 w-5 text-gray-400 mr-1" />}
											size="large"
										/>
										{otpError && (
											<div className="flex items-center gap-1 mt-2 text-red-500 text-sm">
												<AlertCircle className="h-4 w-4" />
												<span>{otpError}</span>
											</div>
										)}
									</div>
									<div className="flex gap-3">
										<Button
											type="primary"
											onClick={handleVerifyOTP}
											loading={verifying}
											className="flex-1 h-12 bg-[#6366F1] hover:bg-[#5558E1] border-0"
										>
											Verify Code
										</Button>
										<Button
											onClick={handleSendOTP}
											loading={sending}
											className="flex-1 h-12 border-[#6366F1] text-[#6366F1] hover:text-[#5558E1] hover:border-[#5558E1]"
										>
											Resend Code
										</Button>
									</div>
								</div>
							)}
						</div>
					)}

					{verified && (
						<div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg mt-2">
							<CheckCircle className="h-5 w-5" />
							<span className="font-medium">Phone number verified successfully</span>
						</div>
					)}

					<Form.Item
						shouldUpdate={() => {
							const isFormChanged = form.isFieldsTouched() !== isTouched;
							if (isFormChanged) {
								setIsTouched(form.isFieldsTouched());
							}
							return isFormChanged;
						}}
						className={`${baseClass}__footer mt-4`}
					>
						{() =>
							form.isFieldsTouched() &&
							isTouched && <Alert type="warning" message={<SaveButton />} />
						}
					</Form.Item>
				</Form>
			</div>
		);
	},
);
